import { useEffect } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { useProducts } from "@/features/products/products-api";
import { BundleWizardData } from "./wizard-form";

export default function StepBasics({
  form,
}: {
  form: UseFormReturn<BundleWizardData>;
}) {
  const { data: regions } = useActiveRegions();
  const { data: products = [] } = useProducts();
  // useWatch (hook) — not form.watch (method) — so this child re-renders when
  // the page's prefill effect sets region_id (which doesn't trigger a nav).
  const selectedRegionId =
    useWatch({ control: form.control, name: "region_id" }) ?? "";
  const selectedRegion = regions?.find((r) => r.id === selectedRegionId);

  // "Purchased for someone else" (gift / on-behalf) products: every bundle is a
  // standard pack paid before scheduling — hide + force both fields (UI-only).
  const productId = useWatch({ control: form.control, name: "product_id" });
  const selectedProduct = products.find((p) => p.id === productId);
  const forSomeoneElse =
    selectedProduct?.participant_config?.purchaser_is_participant === false;
  // Multi-participant products (>1 slot) must also be paid before scheduling —
  // lock (but keep) the payment-timing field.
  const isMultiParticipant =
    (selectedProduct?.participant_config?.slots?.length ?? 0) > 1;

  useEffect(() => {
    if (forSomeoneElse) {
      form.setValue("type", "standard");
      form.setValue("payment_timing", "BEFORE_SCHEDULING");
    } else if (isMultiParticipant) {
      form.setValue("payment_timing", "BEFORE_SCHEDULING");
    }
  }, [forSomeoneElse, isMultiParticipant, form]);

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              Bundle Name <span className="text-red-500">*</span>
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g. 4 Session Therapy Pack" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {forSomeoneElse ? (
        <p className="text-xs text-gray-500">
          "For someone else" products are always standard bundles paid before
          scheduling — type &amp; payment timing are fixed.
        </p>
      ) : (
        <>
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Type <span className="text-red-500">*</span>
                </FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Bundle type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="reschedule">Reschedule</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="payment_timing"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Payment timing</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isMultiParticipant}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="When to take payment" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="BEFORE_SCHEDULING">
                      Pay before scheduling
                    </SelectItem>
                    <SelectItem value="AFTER_SCHEDULING">
                      Pay after scheduling
                    </SelectItem>
                  </SelectContent>
                </Select>
                {isMultiParticipant ? (
                  <p className="text-xs text-gray-500">
                    Multi-participant products must be paid before scheduling.
                  </p>
                ) : null}
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}

      <FormItem>
        <FormLabel>
          Region <span className="text-red-500">*</span>
        </FormLabel>
        <Select
          value={selectedRegionId}
          onValueChange={(id) => {
            const region = regions?.find((r) => r.id === id);
            form.setValue("region_id", id, { shouldValidate: true });
            form.setValue("region", region?.code ?? "");
          }}
        >
          <FormControl>
            <SelectTrigger>
              <SelectValue placeholder="Select a region">
                {selectedRegion
                  ? `${codeToFlag(selectedRegion.code)} ${selectedRegion.display_name}`
                  : undefined}
              </SelectValue>
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            {(regions ?? []).map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {codeToFlag(r.code)} {r.display_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">
          Currency seeds from the region but stays editable in the next step.
        </p>
      </FormItem>

      <FormField
        control={form.control}
        name="status"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Status</FormLabel>
            <Select value={field.value} onValueChange={field.onChange}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Card-display fields (saved into the bundle's metadata). */}
      <FormField
        control={form.control}
        name="tagline"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tagline (card)</FormLabel>
            <FormControl>
              <Input
                placeholder="e.g. BEST VALUE"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="features"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Features (one per line)</FormLabel>
            <FormControl>
              <textarea
                className="w-full min-h-[80px] rounded-md border border-gray-200 p-2 text-sm"
                placeholder={"8 therapy sessions\nFlexible scheduling\nPriority support"}
                value={(field.value ?? []).join("\n")}
                onChange={(e) => field.onChange(e.target.value.split("\n"))}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="isMostPopular"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
            <FormLabel className="text-base">Most popular</FormLabel>
            <FormControl>
              <Switch
                checked={!!field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="show_to_public"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <FormLabel className="text-base">Show to public</FormLabel>
              <p className="text-xs text-gray-500">
                Whether this bundle is publicly visible.
              </p>
            </div>
            <FormControl>
              <Switch
                checked={!!field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />
    </div>
  );
}
